import { useEffect, useRef, useState } from 'react';
import { choiceProp } from './elementProps';
import { useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import { isSafeUrl } from './htmlPolicy';
import { PRODUCT_ART } from './placeholderCatalog';
import { useProduct, useProductVariant } from './productContext';
import { useListingWords } from './listingWords';
import { MediaLightbox, type ViewedPicture } from './MediaLightbox';

import './elementProductPage.css';
import { responsive } from './responsiveImage';
import { PRODUCT_MEDIA_SIZES } from '@xshop/shared';

/** How the pictures are laid out: Sleek's first-large-then-pairs, an even grid, or the first alone. */
const LAYOUTS = ['stack', 'grid', 'single'] as const;

// Follows which picture a phone's swipe rests on, for the "2/6" under it.
function useSwipedPicture(): [React.RefObject<HTMLDivElement>, number, () => void] {
  const track = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState(0);
  const follow = () => {
    const el = track.current;
    const first = el?.firstElementChild as HTMLElement | null;
    if (el && first) setAt(Math.round(Math.abs(el.scrollLeft) / (first.offsetWidth + 10)));
  };
  return [track, at, follow];
}

/** One of the product's pictures. */
type Picture = NonNullable<ReturnType<typeof useProduct>>['images'][number];

// Puts the chosen variant's own picture first, as a Shopify theme features it, keeping the others in order.
function featuredFirst(pictures: readonly Picture[], imageId: string | null | undefined): Picture[] {
  const featured = imageId ? pictures.find((picture) => picture.id === imageId) : undefined;
  return featured ? [featured, ...pictures.filter((picture) => picture !== featured)] : [...pictures];
}

// Draws every picture of the product in scope, each opening the viewer, with a count under a phone's swipe.
export const ProductMediaElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const product = useProduct();
  const { editing } = useElementRender();
  const words = useListingWords();
  const [open, setOpen] = useState<number | null>(null);
  const [track, swiped, follow] = useSwipedPicture();
  const { selected } = useProductVariant();
  const imageId = product?.variants.find((variant) => variant.id === selected)?.imageId;
  // A newly chosen variant's picture is first; a phone's swipe goes back to show it.
  useEffect(() => { track.current?.scrollTo({ left: 0 }); }, [imageId, track]);
  const layout = choiceProp(node.props, 'layout', LAYOUTS, 'stack');
  const sorted = [...(product?.images ?? [])].sort((a, b) => a.position - b.position).filter((picture) => isSafeUrl(picture.src, true));
  const pictures = featuredFirst(sorted, imageId);
  const shown = layout === 'single' ? pictures.slice(0, 1) : pictures;
  const viewed: ViewedPicture[] = shown.map((picture) => ({ src: picture.src, alt: picture.altText || product?.title || '' }));
  return (
    <div className="xe-media-wrap">
      <div {...box} ref={track} onScroll={follow} className={[box.className, 'xe-media'].filter(Boolean).join(' ')} data-layout={layout}>
        {shown.length === 0 && <figure className="xe-media-item"><img src={PRODUCT_ART} alt="" /></figure>}
        {viewed.map((picture, index) => (
          <figure key={shown[index].id} className="xe-media-item">
            <button type="button" className="xe-media-open" aria-label={words.openPicture(index + 1, viewed.length)} onClick={editing ? undefined : () => setOpen(index)}>
              <img {...responsive(picture.src, PRODUCT_MEDIA_SIZES)} alt={picture.alt} loading={index === 0 ? 'eager' : 'lazy'} {...(index === 0 ? { fetchpriority: 'high' } as Record<string, string> : {})} />
            </button>
          </figure>
        ))}
      </div>
      {viewed.length > 1 ? <p className="xe-media-count" aria-hidden="true">{Math.min(swiped, viewed.length - 1) + 1}/{viewed.length}</p> : null}
      {open !== null ? <MediaLightbox pictures={viewed} at={open} onMove={setOpen} onClose={() => setOpen(null)} /> : null}
    </div>
  );
};
